import {
  Box,
  Button,
  CardContent,
  DialogActions,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Label } from 'src/components';
import {
  AlarmPopupCard,
  AlarmPopupDialog,
  DialogContentWithCustomScroll,
  TableContainerWithCustomScroll,
} from '../style';
import { useState } from 'react';
import {
  formatSecondsToDhms,
  formatUTCDateToLocal,
  upperSnakeCaseToTitleCase,
} from 'src/utilities';
import { Notification, PopupProps } from '../../types';
import { SITES_DASHBOARD_PATH } from 'src/routes/config';
import { SitesService } from 'src/services/sites/sites.service';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { useNavigate } from 'react-router-dom';
import { SiteInterface } from 'src/features/sites/types/site';
import { getPriorityColor, renderTitleIcon } from '../../utils';
import { DetailsCell, HeaderStack, NotificationsCellLabel, NotificationsCellValue } from './style';
import { useAuthentication } from 'src/features/authentication/context';
import { AlarmsService } from 'src/services/alarms/alarms.service';
import { ConditionsBox } from '../conditionsBox';
import { LastEditedInfoBox } from '../lastEditedInfoBox';
import { AccordionDeleteAlarmRule } from 'src/features/alarm-rules-details/components/AccordionDeleteAlarmRule';
import { AlertDialogDeleteAlarmRule } from 'src/features/alarm-rules-details/components/AlertDialog';
import { useGetAlarmPopupInfo } from 'src/hooks/apiHooks/useGetAlarmPopupInfo';
import { useGetAlarmPopupNotifications } from 'src/hooks/apiHooks/useGetAlarmPopupNotifications';
import { useToggle } from 'src/hooks/useToggle';
import { ArrowTopRightIcon } from 'src/assets/icons/common/ArrowTopRightIcon';

export const MobileAlarmPopUp = ({
  open,
  onClose,
  customerId,
  siteId,
  alarmData,
  setAlarmData,
  setRefreshTrigger,
  onAcknowledged,
  onAcknowledgedChange,
  handleDeleteAlarmEvent,
  isDeleteAlarmEventAllowed,
}: PopupProps) => {
  const theme = useTheme();
  const { saveSite } = useSite();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const { idToken } = useAuthentication();
  const { state: isDeleteDialogOpen, onToggle: toggleDeleteDialog } = useToggle();

  const { alarmPopupInfo } = useGetAlarmPopupInfo({
    customerId: customerId!,
    siteId: alarmData.siteId,
    eventId: alarmData.id,
    isOpen: open,
  });
  const { notifications } = useGetAlarmPopupNotifications({
    customerId: customerId!,
    eventId: alarmData.id,
    isOpen: open,
  });

  const onAcknowledgeAndClose = () => {
    AlarmsService.acknowledgeAlarm(customerId, siteId, alarmPopupInfo.id, idToken)
      .then((response) => {
        if (typeof setRefreshTrigger === 'function') {
          setRefreshTrigger(true);
        }

        if (typeof onAcknowledgedChange === 'function') {
          onAcknowledgedChange(alarmPopupInfo.id);
        }

        onAcknowledged && onAcknowledged(response);

        setAlarmData((prevData: any) => {
          if (prevData.value === null) {
            return prevData;
          }
          return {
            ...prevData,
            value: prevData.value.map((item: any) => {
              if (item.id === alarmPopupInfo.id) {
                return {
                  ...item,
                  acknowledgment: response.acknowledgment,
                };
              }
              return item;
            }),
          };
        });
      })
      .catch((error) => {
        console.error(error);
      });
    setAcknowledged(false);
    onClose();
  };

  const handleGoToSiteClick = () => {
    SitesService.getById({ customerId: customerId, siteId: siteId })
      .then((data: SiteInterface) => {
        saveSite(data);
      })
      .catch((error) => {
        console.error(error);
      });
    navigate(SITES_DASHBOARD_PATH + `?customerId=${customerId}&siteId=${siteId}`);
  };

  const onHandleClose = () => {
    setAcknowledged(false);
    onClose();
  };

  return (
    <AlarmPopupDialog open={open} onClose={onHandleClose} maxWidth="lg">
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default }}>
        <Stack flexDirection={'column'}>
          <HeaderStack>
            <Stack flexDirection={'row'} alignItems={'center'}>
              <Box
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                data-sm={`alarm-${alarmPopupInfo?.priority}`}
              >
                {renderTitleIcon(alarmPopupInfo, theme)}
              </Box>
              <Typography variant="h6" sx={{ ml: '16px', mr: '8px' }}>
                Alarm - {upperSnakeCaseToTitleCase(alarmPopupInfo?.priority || '')}
              </Typography>
            </Stack>
            <Label
              variant="filled"
              color={getPriorityColor(alarmPopupInfo?.priority, alarmPopupInfo?.status)}
            >
              {alarmPopupInfo?.status}
            </Label>
          </HeaderStack>
          <Button variant="outlined" endIcon={<ArrowTopRightIcon />} onClick={handleGoToSiteClick}>
            Go to Site
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContentWithCustomScroll sx={{ backgroundColor: theme.palette.background.default }}>
        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Site Details:</Typography>
            <TableContainer sx={{ mt: '12px', overflow: 'hidden' }}>
              <Table>
                <TableBody>
                  <TableRow data-sm="site-name">
                    <DetailsCell align="left">Site Name:</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell align="left" data-sm={alarmPopupInfo?.siteName}>
                      <strong>{alarmPopupInfo?.siteName}</strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="assets">
                    <DetailsCell align="left">Asset:</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell align="left" data-sm="assets-names">
                      {alarmPopupInfo?.assets?.map((asset, i) => {
                        const isLastElement = alarmPopupInfo.assets?.length === i + 1;
                        return (
                          <strong key={asset.id} data-sm={asset?.name}>
                            {asset?.name ?? '-'}
                            {isLastElement ? '.' : ', '}
                          </strong>
                        );
                      })}
                      {!alarmPopupInfo.assets && (
                        <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                          No assets assigned to this event
                        </Typography>
                      )}
                    </DetailsCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </AlarmPopupCard>

        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Event Details:</Typography>
            <TableContainer sx={{ mt: '12px' }}>
              <Table>
                <TableBody>
                  <TableRow data-sm="alarm-title">
                    <DetailsCell align="left">Alarm Title:</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell align="left" data-sm={alarmPopupInfo?.title}>
                      <strong>{alarmPopupInfo?.title}</strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="start-date">
                    <DetailsCell align="left">Start (date / time):</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell
                      align="left"
                      data-sm={
                        alarmPopupInfo?.startDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.startDate)
                          : '-'
                      }
                    >
                      <strong>
                        {alarmPopupInfo?.startDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.startDate)
                          : '-'}
                      </strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="end-date">
                    <DetailsCell align="left">End (date / time):</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell
                      align="left"
                      data-sm={
                        alarmPopupInfo?.endDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.endDate)
                          : '-'
                      }
                    >
                      <strong>
                        {alarmPopupInfo?.endDate !== null
                          ? formatUTCDateToLocal(false, alarmPopupInfo?.endDate)
                          : '-'}
                      </strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="duration">
                    <DetailsCell align="left">Duration:</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell
                      align="left"
                      data-sm={
                        +alarmPopupInfo.durationInSeconds > 0
                          ? formatSecondsToDhms(+alarmPopupInfo.durationInSeconds)
                          : '-'
                      }
                    >
                      <strong>
                        {+alarmPopupInfo.durationInSeconds > 0
                          ? formatSecondsToDhms(+alarmPopupInfo.durationInSeconds)
                          : '-'}
                      </strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="description">
                    <DetailsCell align="left">Description:</DetailsCell>
                  </TableRow>
                  <TableRow>
                    <DetailsCell align="left" data-sm={alarmPopupInfo?.description ?? '-'}>
                      <strong>{alarmPopupInfo?.description ?? '-'}</strong>
                    </DetailsCell>
                  </TableRow>

                  <TableRow data-sm="acknowledgement">
                    <DetailsCell align="left">
                      <FormControlLabel
                        value="end"
                        control={
                          <Switch
                            checked={alarmPopupInfo?.acknowledgement ? true : acknowledged}
                            disabled={!!alarmPopupInfo?.acknowledgement}
                            color="primary"
                            onChange={(event, checked) => {
                              setAcknowledged(checked);
                            }}
                          />
                        }
                        label={
                          !!alarmPopupInfo?.acknowledgement || acknowledged
                            ? 'Acknowledged'
                            : 'Acknowledge'
                        }
                        labelPlacement="end"
                      />
                    </DetailsCell>
                  </TableRow>
                  {alarmPopupInfo?.acknowledgement ? (
                    <TableRow>
                      <DetailsCell align="left" sx={{ verticalAlign: 'inherit' }}>
                        Acknowledged by:{' '}
                        <strong>
                          {alarmPopupInfo?.acknowledgement?.firstName}{' '}
                          {alarmPopupInfo?.acknowledgement?.lastName},{' '}
                          {formatUTCDateToLocal(false, alarmPopupInfo?.acknowledgement?.timestamp)}
                        </strong>
                      </DetailsCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </AlarmPopupCard>

        <AlarmPopupCard>
          <CardContent sx={{ p: '16px!important' }}>
            <Typography variant="h6">Notifications:</Typography>
            <TableContainerWithCustomScroll sx={{ mt: '12px', maxHeight: '125px' }}>
              <Table>
                <TableBody data-sm="notifications-table-body">
                  {notifications.map((notification: Notification) => (
                    <TableRow key={notification?.id ?? notification?.createdAt}>
                      <NotificationsCellLabel align="left">
                        <strong>{formatUTCDateToLocal(false, notification?.createdAt)}</strong>
                      </NotificationsCellLabel>
                      <NotificationsCellValue align="left">
                        <strong>{notification?.firstName + ' ' + notification?.lastName}</strong>
                      </NotificationsCellValue>
                    </TableRow>
                  ))}
                  {!notifications.length && (
                    <Typography data-sm="notifications-message" sx={{ fontSize: '14px' }}>
                      No notifications have been sent for this event
                    </Typography>
                  )}
                </TableBody>
              </Table>
            </TableContainerWithCustomScroll>
          </CardContent>
        </AlarmPopupCard>

        <ConditionsBox
          clearingInfo={alarmPopupInfo?.clearingInfo}
          triggerInfo={alarmPopupInfo?.triggerInfo}
          type={alarmPopupInfo?.type}
        />
        {isDeleteAlarmEventAllowed && (
          <Box sx={{ padding: '0rem 0.4rem 1rem 0.4rem' }}>
            <AccordionDeleteAlarmRule
              title="Alarm event options"
              handleClick={toggleDeleteDialog}
              description="Clicking 'Delete' button will permanently delete current alarm event."
            />
          </Box>
        )}
      </DialogContentWithCustomScroll>
      <Divider />
      <DialogActions
        data-sm="alarm-popup-footer"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <Stack flexDirection={'column'} sx={{ width: '100%' }}>
          <Typography sx={{ color: theme.palette.grey[600] }} variant="body2">
            Alarm ID: {alarmPopupInfo.id}
          </Typography>
          <Box sx={{ margin: '0.5rem 0' }}>
            <LastEditedInfoBox
              updatedBy={alarmPopupInfo?.updatedBy}
              updatedAt={alarmPopupInfo?.updatedAt}
            />
          </Box>
          {acknowledged ? (
            <Button onClick={onAcknowledgeAndClose} variant="outlined" fullWidth sx={{ mt: '8px' }}>
              Acknowledge & Close
            </Button>
          ) : (
            <Button onClick={onHandleClose} variant="outlined" fullWidth sx={{ mt: '8px' }}>
              Close
            </Button>
          )}
        </Stack>
      </DialogActions>
      <AlertDialogDeleteAlarmRule
        dialogTitle="Delete Alarm Event"
        AlarmRuleDeleteToggleState={isDeleteDialogOpen}
        onDeleteAlarmRuleToggle={toggleDeleteDialog}
        handleDelete={() =>
          handleDeleteAlarmEvent({
            customerId: customerId,
            siteId: siteId,
            alarmId: alarmPopupInfo.id,
          })
        }
        title={alarmPopupInfo?.title || ''}
        isAlarmRule={false}
      />
    </AlarmPopupDialog>
  );
};
